import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { showSnackbar } from '../Reducer/SnackbarReducer';
import { BaseInfoActions } from '../Reducer/BaseInfoReducer';
import Commons from '../Services/Common/Common';
import Global from '../Services/Common/Global';
import { apiRequest } from '../Services/Common/ApiRequest';
import { mapParamsBySchema } from '../util';
import {
  setSearchInfo,
  setCustCd,
  setDepList,
  setDepCd,
} from '../Reducer/SearchReducer';
import {
  FetchAdminCompanyMasterRows,
  FetchCustRowsByCoCd,
  IsAdminUser,
  MapCompanyRowsToCoList,
  MapCustRowsToCustList,
  NormalizeCustRow,
} from './adminCompanyMaster';

const BuildCustFromUserInfo = (user, coCd) => {
  const cd = user?.custCd ?? user?.CustCd;
  if (!cd) return [];
  return [
    NormalizeCustRow({
      coCd: coCd ?? user?.coCd ?? user?.CoCd ?? '',
      custCd: cd,
      custNm: user?.custNm ?? user?.CustNm ?? cd,
    }),
  ];
};

export default function useCompanyCustDepRedux({
  requireCompany = false,
  requireCust = false,
  requireDep = false,
  autoFetchDep = false,
  autoFetchDepDefaultAll = false,
  defaultCustAll = false,
  defaultCoAll = false,
  /** ADMIN 홈 등 — 초기 진입 시 회사·사업장 자동 선택 없음 */
  noDefaultCompanyOnInit = false,
}) {
  const dispatch = useDispatch();
  const { companyInfo, custInfo, userInfo } = useSelector(
    (state) => state.BaseInfo
  );
  const searchInfo = useSelector((state) => state.SearchInfo);
  const isAdmin = IsAdminUser(userInfo);
  const [adminCompanyReady, setAdminCompanyReady] = useState(!isAdmin);

  const ResolveCustRows = async (coCd) => {
    /** ADMIN: 로그인/GetInfo custInfo 캐시 대신 회사별 최신 사업장 조회 */
    if (isAdmin) {
      if (!coCd) return [];
      return FetchCustRowsByCoCd(coCd, userInfo);
    }
    if (custInfo?.length) return custInfo.map(NormalizeCustRow);
    const fromUser = BuildCustFromUserInfo(userInfo, coCd);
    if (fromUser.length) return fromUser;
    if (!coCd) return [];
    return FetchCustRowsByCoCd(coCd, userInfo);
  };

  /** ADMIN — 회사 마스터가 비어 있을 때만 조회 (Redux·캐시 활용) */
  useEffect(() => {
    if (!isAdmin || !userInfo?.userid) {
      setAdminCompanyReady(true);
      return;
    }

    if (companyInfo?.length > 0) {
      setAdminCompanyReady(true);
      return;
    }

    let cancelled = false;
    setAdminCompanyReady(false);

    (async () => {
      const rows = await FetchAdminCompanyMasterRows(userInfo);
      if (cancelled) return;
      if (rows.length > 0) {
        dispatch(BaseInfoActions.setCompanyInfo(rows));
      }
      setAdminCompanyReady(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [dispatch, isAdmin, userInfo, companyInfo?.length]);

  const getDepLastName = async (_custCd) => {
    try {
      const encryptedParams = mapParamsBySchema({
        CustCd: (_, context) => Commons.EncryptAES(_custCd || '') || '',
        SearchText: (_, context) => Commons.EncryptAES('') || '',
      });

      const result = await apiRequest(
        'post',
        Global.urls.Utility.getDepLast,
        encryptedParams
      );

      const data = result?.data;

      if (!data || data.length === 0 || data[0]?.type === 'Error') {
        return null;
      }
      return data;
    } catch (e) {
      console.error(e.message);
      return null;
    }
  };

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const companyReady = Array.isArray(companyInfo);

      if (isAdmin && !adminCompanyReady) {
        return;
      }

      if (!companyInfo?.length && requireCompany) {
        if (!companyReady) return;
        if (isAdmin) return;
        dispatch(
          showSnackbar({
            message: '회사 정보를 불러올 수 없습니다.',
            type: 'error',
          })
        );
        return;
      }

      const stored = Commons.Storages('get', 'companyInfo') || {};

      const hasStoredCoCd =
        stored && typeof stored === 'object' && 'coCd' in stored;
      let defaultCoCd = hasStoredCoCd
        ? (stored.coCd ?? '')
        : defaultCoAll || noDefaultCompanyOnInit
          ? ''
          : (companyInfo?.[0]?.coCd ?? companyInfo?.[0]?.CoCd ?? '');

      if (!defaultCoCd && isAdmin && !noDefaultCompanyOnInit) {
        defaultCoCd = searchInfo?.coCd || '';
      }

      const coList = MapCompanyRowsToCoList(companyInfo).map((c) => ({
        value: c.value,
        name: c.name,
        ceoName: c.ceoName,
      }));

      const canReuseAdminCustList =
        isAdmin &&
        defaultCoCd &&
        String(searchInfo?.coCd || '') === String(defaultCoCd) &&
        (searchInfo?.custList?.length ?? 0) > 0;

      const resolvedCustRows = canReuseAdminCustList
        ? searchInfo.custList.map((item) =>
            NormalizeCustRow({
              coCd: defaultCoCd,
              custCd: item.value,
              custNm: item.name,
            })
          )
        : await ResolveCustRows(defaultCoCd);
      if (cancelled) return;

      if (!resolvedCustRows.length && requireCust) {
        if (!companyReady) return;
        if (defaultCoCd) {
          if (companyInfo.length > 0) {
            dispatch(
              showSnackbar({
                message: '사업장 정보를 불러올 수 없습니다.',
                type: 'error',
              })
            );
          }
          return;
        }
      }

      const nextCustList = MapCustRowsToCustList(resolvedCustRows, defaultCoCd);
      const hasStoredCustCd =
        stored && typeof stored === 'object' && 'custCd' in stored;
      let defaultCustCd = hasStoredCustCd
        ? (stored.custCd ?? '')
        : defaultCustAll
          ? ''
          : (nextCustList?.[0]?.value ?? '');

      if (
        isAdmin &&
        defaultCustCd &&
        !nextCustList.some((item) => String(item.value) === String(defaultCustCd))
      ) {
        defaultCustCd = nextCustList?.[0]?.value ?? '';
      }

      if (!defaultCoCd) {
        defaultCustCd = '';
      }

      if (requireCust && nextCustList.length === 0 && defaultCoCd) {
        dispatch(
          showSnackbar({
            message: '선택한 회사에 사업장이 없습니다.',
            type: 'error',
          })
        );
      }

      const nextSearchPayload = {
        coList,
        coCd: defaultCoCd,
        custList: nextCustList,
        custCd: defaultCustCd,
      };

      const isSameSearchInfo =
        String(searchInfo?.coCd ?? '') ===
          String(nextSearchPayload.coCd ?? '') &&
        String(searchInfo?.custCd ?? '') ===
          String(nextSearchPayload.custCd ?? '') &&
        JSON.stringify(searchInfo?.coList ?? []) ===
          JSON.stringify(nextSearchPayload.coList) &&
        JSON.stringify(searchInfo?.custList ?? []) ===
          JSON.stringify(nextSearchPayload.custList);

      if (!isSameSearchInfo) {
        dispatch(setSearchInfo(nextSearchPayload));
      }

      if ((requireDep || autoFetchDep) && defaultCustCd) {
        const depData = await getDepLastName(defaultCustCd);
        if (cancelled) return;
        let depList = [];

        if (depData && depData.length > 0) {
          depList = depData.map((item) => ({
            name: item.depNmLast,
            value: item.depCd,
          }));
        } else if (requireDep) {
          dispatch(
            showSnackbar({
              message: '부서 정보를 불러올 수 없습니다.',
              type: 'error',
            })
          );
        }
        dispatch(setDepList(depList));

        if (autoFetchDep && autoFetchDepDefaultAll && !requireDep) {
          dispatch(setDepCd(''));
        } else if (requireDep || (autoFetchDep && depList.length > 0)) {
          const defaultDepCd = stored.depCd || (depList?.[0]?.value ?? '');
          dispatch(setDepCd(defaultDepCd));
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    companyInfo,
    custInfo,
    userInfo,
    isAdmin,
    adminCompanyReady,
    requireCompany,
    requireCust,
    requireDep,
    autoFetchDep,
    autoFetchDepDefaultAll,
    defaultCustAll,
    defaultCoAll,
    noDefaultCompanyOnInit,
    dispatch,
  ]);

  const validateSelection = () => {
    if (requireCompany && !searchInfo.coCd) {
      dispatch(showSnackbar({ message: '회사를 선택하세요.', type: 'error' }));
      return false;
    }
    if (requireCust && !searchInfo.custCd) {
      dispatch(
        showSnackbar({ message: '사업장을 선택하세요.', type: 'error' })
      );
      return false;
    }
    if (requireDep && !searchInfo.depCd) {
      dispatch(showSnackbar({ message: '부서를 선택하세요.', type: 'error' }));
      return false;
    }
    return true;
  };

  const handleCoChange = async (raw) => {
    const newCoCd = raw?.target?.value !== undefined ? raw.target.value : raw;
    const resolvedRows = await ResolveCustRows(newCoCd);
    const nextCustList = MapCustRowsToCustList(resolvedRows, newCoCd);
    const nextCustCd =
      requireCust && nextCustList.length > 0 ? nextCustList[0].value : '';

    if (requireCust && nextCustList.length === 0) {
      dispatch(
        showSnackbar({
          message: '선택한 회사에 사업장이 없습니다.',
          type: 'error',
        })
      );
    }

    dispatch(
      setSearchInfo({
        coCd: newCoCd,
        custList: nextCustList,
        custCd: nextCustCd,
      })
    );

    if (requireDep || autoFetchDep) {
      dispatch(setDepList([]));
      dispatch(setDepCd(''));

      if (nextCustCd) {
        const depData = await getDepLastName(nextCustCd);
        let depList = [];

        if (depData && depData.length > 0) {
          depList = depData.map((item) => ({
            name: item.depNmLast,
            value: item.depCd,
          }));
        } else if (requireDep) {
          dispatch(
            showSnackbar({
              message: '부서 정보를 불러올 수 없습니다.',
              type: 'error',
            })
          );
        }

        dispatch(setDepList(depList));
        if (autoFetchDep && autoFetchDepDefaultAll && !requireDep) {
          dispatch(setDepCd(''));
        } else if (requireDep || (autoFetchDep && depList.length > 0)) {
          dispatch(setDepCd(depList?.[0]?.value ?? ''));
        }
      }
    }

    Commons.Storages('set', 'companyInfo', {
      coCd: newCoCd || '',
      custCd: nextCustCd || '',
      depCd: '',
    });

    return nextCustCd;
  };

  const handleCustChange = async (raw) => {
    const newCustCd = raw?.target?.value !== undefined ? raw.target.value : raw;
    dispatch(setCustCd(newCustCd));
    Commons.Storages('set', 'companyInfo', {
      coCd: searchInfo.coCd || '',
      custCd: newCustCd,
      depCd: '',
    });

    if (requireDep || autoFetchDep) {
      const depData = await getDepLastName(newCustCd);
      let depList = [];

      if (depData && depData.length > 0) {
        depList = depData.map((item) => ({
          name: item.depNmLast,
          value: item.depCd,
        }));
      } else if (requireDep) {
        dispatch(
          showSnackbar({
            message: '부서 정보를 불러올 수 없습니다.',
            type: 'error',
          })
        );
      }

      dispatch(setDepList(depList));
      if (autoFetchDep && autoFetchDepDefaultAll && !requireDep) {
        dispatch(setDepCd(''));
      } else if (requireDep || (autoFetchDep && depList.length > 0)) {
        dispatch(setDepCd(depList?.[0]?.value ?? ''));
      }
    }

    return newCustCd;
  };

  const handleDepChange = (newDepCd) => {
    dispatch(setDepCd(newDepCd));
    Commons.Storages('set', 'companyInfo', {
      coCd: searchInfo.coCd || '',
      custCd: searchInfo.custCd || '',
      depCd: newDepCd || '',
    });
  };

  return {
    handleCoChange,
    handleCustChange,
    handleDepChange,
    validateSelection,
  };
}
